import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shield, ArrowLeft, X, Mail, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PrivacyModal from "@/components/PrivacyModal";
import TermsModal from "@/components/TermsModal";
import TotpChallengeStep from "@/components/admin/TotpChallengeStep";

export function LoginScreen({ onLoggedIn }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [showForgot, setShowForgot] = useState(false);
    const [forgotEmail, setForgotEmail] = useState("");
    const [forgotSent, setForgotSent] = useState(false);
    const [forgotBusy, setForgotBusy] = useState(false);
    const [privacyOpen, setPrivacyOpen] = useState(false);
    const [termsOpen, setTermsOpen] = useState(false);
    // iter87.199.43 — TOTP challenge state.
    const [challenge, setChallenge] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        if (!email || !password) { toast.error("Email and password required"); return; }
        setLoading(true);
        try {
            const data = await api.authLogin({ email: email.trim(), password });
            if (data?.requires_2fa) {
                setChallenge({
                    challenge_token: data.challenge_token,
                    expires_in: data.expires_in,
                    admin_email: data.admin_email,
                });
                setLoading(false);
                return;
            }
            onLoggedIn(data.admin);
        } catch (err) {
            const msg = err.response?.data?.detail;
            toast.error(typeof msg === "string" ? msg : "Login failed");
        }
        setLoading(false);
    };

    const submitForgot = async (e) => {
        e.preventDefault();
        if (!forgotEmail.trim()) { toast.error("Enter your email"); return; }
        setForgotBusy(true);
        try {
            await api.authRequestPasswordReset(forgotEmail.trim().toLowerCase());
            setForgotSent(true);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to send reset link");
        }
        setForgotBusy(false);
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-4">
            <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
            <div className="absolute -top-32 -right-32 w-96 h-96 bg-volt/10 blur-3xl rounded-full" />

            <div className="brutal-card p-7 lg:p-10 w-full max-w-md relative z-10">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-volt border-2 border-black shadow-brutal flex items-center justify-center">
                        <Shield className="w-5 h-5 text-white" strokeWidth={2.5} />
                    </div>
                    <div>
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500">Restricted</p>
                        <h1 className="font-display font-black text-2xl">Site Admin</h1>
                    </div>
                </div>

                <form onSubmit={submit} className="space-y-4" style={{ display: challenge ? "none" : undefined }}>
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Email</label>
                        <input
                            data-testid="admin-login-email-input"
                            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@example.com"
                            className="brutal-input w-full" autoFocus />
                    </div>
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Password</label>
                        <input
                            data-testid="admin-login-password-input"
                            type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••••"
                            className="brutal-input w-full" />
                    </div>
                    <button
                        data-testid="admin-login-submit-btn"
                        type="submit" disabled={loading}
                        className="brutal-btn-secondary w-full disabled:opacity-60">
                        {loading ? "Signing in..." : "Sign in"}
                    </button>
                </form>

                {challenge && (
                    <TotpChallengeStep
                        challenge={challenge}
                        onSuccess={(r) => onLoggedIn(r.admin)}
                        onCancel={() => setChallenge(null)}
                    />
                )}

                <button
                    type="button"
                    data-testid="admin-forgot-password-btn"
                    onClick={() => { setShowForgot(true); setForgotEmail(email); setForgotSent(false); }}
                    className="text-xs text-zinc-400 hover:text-cyber mt-4 mx-auto flex items-center gap-1"
                    style={{ display: challenge ? "none" : undefined }}
                >
                    <KeyRound className="w-3 h-3" /> Forgot password?
                </button>

                <Link to="/" className="text-xs text-zinc-500 hover:text-cyber mt-4 flex items-center gap-1">
                    <ArrowLeft className="w-3 h-3" /> Back to home
                </Link>

                <div className="mt-6 pt-4 border-t border-[#222] flex items-center justify-center gap-3 text-[10px] text-zinc-500">
                    <button
                        type="button"
                        data-testid="login-terms-link"
                        onClick={() => setTermsOpen(true)}
                        className="hover:text-cyber underline-offset-2 hover:underline"
                    >
                        Terms of Use
                    </button>
                    <span className="text-zinc-700">·</span>
                    <button
                        type="button"
                        data-testid="login-privacy-link"
                        onClick={() => setPrivacyOpen(true)}
                        className="hover:text-cyber underline-offset-2 hover:underline"
                    >
                        Privacy
                    </button>
                </div>
            </div>

            <PrivacyModal open={privacyOpen} onClose={() => setPrivacyOpen(false)} />
            <TermsModal open={termsOpen} onClose={() => setTermsOpen(false)} onAccept={() => setTermsOpen(false)} />

            {showForgot && (
                <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setShowForgot(false)}>
                    <div onClick={(e) => e.stopPropagation()} className="brutal-card p-6 max-w-sm w-full" data-testid="admin-forgot-modal">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-display font-extrabold text-lg uppercase">Reset password</h3>
                            <button onClick={() => setShowForgot(false)} className="brutal-btn-ghost p-2"><X className="w-4 h-4" /></button>
                        </div>
                        {!forgotSent ? (
                            <form onSubmit={submitForgot} className="space-y-3">
                                <p className="text-xs text-zinc-400">Enter your site-admin email. If it matches, we'll send a one-time reset link (expires in 30 minutes).</p>
                                <input
                                    data-testid="admin-forgot-email-input"
                                    type="email" value={forgotEmail} required
                                    onChange={(e) => setForgotEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    className="brutal-input w-full"
                                />
                                <button
                                    data-testid="admin-forgot-submit-btn"
                                    type="submit" disabled={forgotBusy}
                                    className="brutal-btn-primary w-full disabled:opacity-60"
                                >
                                    {forgotBusy ? "Sending..." : "Send reset link"} <Mail className="w-4 h-4" />
                                </button>
                            </form>
                        ) : (
                            <div className="text-center space-y-2" data-testid="admin-forgot-sent-state">
                                <div className="w-10 h-10 mx-auto bg-cyber border-2 border-black shadow-brutal flex items-center justify-center">
                                    <Mail className="w-5 h-5 text-black" strokeWidth={2.5} />
                                </div>
                                <h4 className="font-display font-black text-base">Check your inbox</h4>
                                <p className="text-xs text-zinc-400">If <span className="text-cyber">{forgotEmail}</span> matches a site admin, a reset link is on its way. It expires in 30 minutes.</p>
                                <button onClick={() => setShowForgot(false)} className="brutal-btn-ghost text-xs">Done</button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}


export function InviteModal({ onClose, onCreated, inviterRole, inviterTenantId }) {
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    // iter87.199.44 — Method toggle. Default OFF (magic-link email is
    // opt-in) so existing muscle memory (set-password-directly) keeps
    // working. Flip ON to send a magic-link email via the tenant-admin
    // invite endpoint — the invitee sets their own password on accept.
    const [sendMagicLink, setSendMagicLink] = useState(false);
    // iter70 — Platform admins can pick the target tenant. Tenant admins
    // don't see this field; their invites always land in their own tenant.
    const isPlatformAdmin = inviterRole === "platform_admin";
    const [tenantId, setTenantId] = useState(inviterTenantId || "");
    const [tenantsList, setTenantsList] = useState([]);

    useEffect(() => {
        if (!isPlatformAdmin) return;
        let active = true;
        api.listTenants()
            .then((r) => {
                if (!active) return;
                // Only allow active tenants as invite targets — backend
                // will reject suspended/archived anyway; surface it sooner.
                const usable = (r.tenants || []).filter((t) => (t.lifecycle_state || "active") === "active");
                setTenantsList(usable);
                if (!tenantId && usable.length > 0) {
                    setTenantId(inviterTenantId || usable[0].id);
                }
            })
            .catch(() => { /* dropdown stays empty — fall back to inviter's tenant */ });
        return () => { active = false; };
        // tenantId intentionally excluded — re-running this on every tenantId
        // change (which THIS effect itself sets) would re-fetch the tenant
        // list on every selection instead of once per platform-admin mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPlatformAdmin, inviterTenantId]);

    const submit = async (e) => {
        e.preventDefault();
        // Magic-link mode: no password required — the invitee sets one on accept.
        if (!sendMagicLink && password.length < 8) {
            toast.error("Password must be 8+ characters");
            return;
        }
        const targetTenantId = (isPlatformAdmin && tenantId) ? tenantId : inviterTenantId;
        setLoading(true);
        try {
            if (sendMagicLink) {
                if (!targetTenantId) {
                    toast.error("No tenant selected — refresh and try again");
                    setLoading(false);
                    return;
                }
                const r = await api.inviteTenantAdmin(targetTenantId, email.trim(), name.trim() || undefined);
                toast.success(
                    r?.email_sent
                        ? `Invite email sent to ${email}`
                        : `Invite created — email delivery failed, share the link below manually`
                );
            } else {
                const body = { email: email.trim(), name: name.trim() || undefined, password };
                if (isPlatformAdmin && tenantId && tenantId !== inviterTenantId) {
                    body.tenant_id = tenantId;
                }
                await api.authInvite(body);
                toast.success(`Invited ${email} — share the password with them via secure DM`);
            }
            onCreated?.();
            onClose();
        } catch (err) {
            const msg = err.response?.data?.detail;
            toast.error(typeof msg === "string" ? msg : "Failed");
        }
        setLoading(false);
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
            <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="brutal-card p-6 max-w-md w-full space-y-4">
                <div className="flex items-center justify-between">
                    <h3 className="font-display font-extrabold text-xl">Invite Site Admin</h3>
                    <button type="button" onClick={onClose} className="brutal-btn-ghost p-2"><X className="w-4 h-4" /></button>
                </div>
                <div>
                    <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Email</label>
                    <input data-testid="invite-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="brutal-input w-full" required />
                </div>
                <div>
                    <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Name (optional)</label>
                    <input data-testid="invite-name-input" value={name} onChange={(e) => setName(e.target.value)} className="brutal-input w-full" />
                </div>
                {isPlatformAdmin && tenantsList.length > 0 && (
                    <div>
                        <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">
                            Tenant
                            <span className="ml-1 text-zinc-600 normal-case font-normal">(platform admin only)</span>
                        </label>
                        <select
                            data-testid="invite-tenant-select"
                            value={tenantId}
                            onChange={(e) => setTenantId(e.target.value)}
                            className="brutal-input w-full"
                        >
                            {tenantsList.map((t) => (
                                <option key={t.id} value={t.id}>
                                    {t.name}{t.is_default ? " (default)" : ""}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
                <label className="flex items-start gap-2 text-xs text-zinc-300 border border-cyber/30 bg-cyber/5 p-3">
                    <input
                        type="checkbox"
                        checked={sendMagicLink}
                        onChange={(e) => setSendMagicLink(e.target.checked)}
                        data-testid="invite-magic-link-toggle"
                        className="mt-0.5"
                    />
                    <span>
                        <span className="font-black uppercase tracking-widest text-[10px] text-cyber block">
                            Send a magic-link email instead
                        </span>
                        The invitee gets a click-to-accept email and sets their own password. Recommended — no need to share a password over Slack/SMS. Link expires in 7 days.
                    </span>
                </label>
                {!sendMagicLink && (
                    <div data-testid="invite-password-block">
                        <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Temporary password (8+ chars)</label>
                        <input data-testid="invite-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="brutal-input w-full" required minLength={8} />
                        <p className="text-[10px] text-zinc-500 mt-1">
                            You'll need to share this password with the invitee via secure DM — no email is sent in this mode.
                        </p>
                    </div>
                )}
                <button data-testid="invite-submit-btn" type="submit" disabled={loading} className="brutal-btn-secondary w-full">
                    {loading ? (sendMagicLink ? "Sending…" : "Creating…") : (sendMagicLink ? "Send magic-link email" : "Create with password")}
                </button>
            </form>
        </div>
    );
}


export function ChangePasswordModal({ onClose }) {
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (next.length < 8) { toast.error("Password must be 8+ characters"); return; }
        setLoading(true);
        try {
            await api.authChangePassword({ current_password: current, new_password: next });
            toast.success("Password changed");
            onClose();
        } catch (err) {
            const msg = err.response?.data?.detail;
            toast.error(typeof msg === "string" ? msg : "Failed");
        }
        setLoading(false);
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
            <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="brutal-card p-6 max-w-md w-full space-y-4">
                <div className="flex items-center justify-between">
                    <h3 className="font-display font-extrabold text-xl">Change Password</h3>
                    <button type="button" onClick={onClose} className="brutal-btn-ghost p-2"><X className="w-4 h-4" /></button>
                </div>
                <div>
                    <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Current password</label>
                    <input data-testid="current-password-input" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className="brutal-input w-full" required />
                </div>
                <div>
                    <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">New password (8+ chars)</label>
                    <input data-testid="new-password-input" type="password" value={next} onChange={(e) => setNext(e.target.value)} className="brutal-input w-full" required minLength={8} />
                </div>
                <button data-testid="change-password-submit-btn" type="submit" disabled={loading} className="brutal-btn-primary w-full">
                    {loading ? "Saving..." : "Update password"}
                </button>
            </form>
        </div>
    );
}


export function StatCard({ label, value, icon: Icon, color, testId }) {
    return (
        <div className="brutal-card p-4" data-testid={testId}>
            <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</p>
                <Icon className={`w-4 h-4 ${color}`} strokeWidth={2.5} />
            </div>
            <p className="font-mono font-black text-3xl">{value ?? "—"}</p>
        </div>
    );
}
